import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { type Rarity, rarityColour } from "@/lib/achievementEngine";

type VisitDetail = {
  id: string;
  caption: string | null;
  photo_urls: string[] | null;
  confirmed_at: string;
  restaurant: {
    name: string;
    restaurant_cuisine_types:
      | { cuisine_types: { name: string; emoji: string | null } | null }[]
      | null;
  } | null;
  dishes: { id: string; name: string }[];
  badges: {
    id: string;
    name: string;
    description: string;
    rarity: Rarity;
    colour_hex: string;
  }[];
};

export function VisitDetailSheet({
  visitId,
  open,
  onClose,
}: {
  visitId: string | null;
  open: boolean;
  onClose: () => void;
}) {
  const [data, setData] = useState<VisitDetail | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !visitId) return;
    setLoading(true);
    (async () => {
      const { data: v } = await supabase
        .from("visits")
        .select(
          "id, caption, photo_urls, confirmed_at, dish_ids, restaurant:restaurants(name, restaurant_cuisine_types(cuisine_types(name, emoji)))",
        )
        .eq("id", visitId)
        .maybeSingle();
      if (!v) {
        setData(null);
        setLoading(false);
        return;
      }
      const dishIds = ((v as any).dish_ids ?? []) as string[];
      const [dishesRes, badgesRes] = await Promise.all([
        dishIds.length > 0
          ? supabase.from("dishes").select("id, name").in("id", dishIds)
          : Promise.resolve({ data: [] as any[] }),
        supabase
          .from("user_achievements")
          .select("achievement:achievements(id, name, description, rarity, colour_hex)")
          .eq("trigger_visit_id", visitId),
      ]);
      setData({
        id: (v as any).id,
        caption: (v as any).caption,
        photo_urls: (v as any).photo_urls,
        confirmed_at: (v as any).confirmed_at,
        restaurant: (v as any).restaurant,
        dishes: (dishesRes.data ?? []) as any,
        badges: ((badgesRes.data ?? []) as any[])
          .map((r) => r.achievement)
          .filter(Boolean),
      });
      setLoading(false);
    })();
  }, [open, visitId]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end">
      <button
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/40 animate-in fade-in duration-200"
      />
      <div
        className="relative w-full max-h-[88vh] flex flex-col animate-in slide-in-from-bottom duration-300"
        style={{
          backgroundColor: "var(--colour-surface)",
          borderTopLeftRadius: "var(--radius-lg)",
          borderTopRightRadius: "var(--radius-lg)",
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
      >
        <div className="pt-2.5 pb-1 flex justify-center shrink-0">
          <div
            className="w-10 h-1 rounded-full"
            style={{ backgroundColor: "var(--colour-border)" }}
          />
        </div>
        <div className="flex items-center justify-between px-4 h-12 shrink-0">
          <span className="w-11" />
          <h2 className="text-[16px] font-semibold">Visit</h2>
          <button
            aria-label="Close"
            onClick={onClose}
            className="w-11 h-11 -mr-3 flex items-center justify-center"
          >
            <X size={22} />
          </button>
        </div>

        <div className="overflow-y-auto px-4 pb-6">
          {loading || !data ? (
            <div className="h-64 rounded-[var(--radius-md)] animate-pulse"
              style={{ backgroundColor: "var(--colour-surface-raised)" }} />
          ) : (
            <>
              <h3 className="text-[22px] font-bold">{data.restaurant?.name}</h3>
              {(() => {
                const c = data.restaurant?.restaurant_cuisine_types?.[0]?.cuisine_types;
                if (!c) return null;
                return (
                  <p className="text-[14px] text-[var(--colour-text-secondary)] mt-1">
                    {c.emoji ?? "🍽️"} {c.name}
                  </p>
                );
              })()}

              {data.photo_urls && data.photo_urls[0] && (
                <img
                  src={data.photo_urls[0]}
                  alt=""
                  className="mt-4 w-full aspect-[4/3] object-cover rounded-[var(--radius-md)]"
                />
              )}

              {data.caption && (
                <p className="mt-4 text-[15px] whitespace-pre-wrap">{data.caption}</p>
              )}

              {data.dishes.length > 0 && (
                <div className="mt-5">
                  <h4 className="text-[14px] font-semibold mb-2">Dishes</h4>
                  <div className="flex flex-wrap gap-2">
                    {data.dishes.map((d) => (
                      <span
                        key={d.id}
                        className="px-3 h-8 inline-flex items-center rounded-[var(--radius-full)] text-[13px] border"
                        style={{
                          borderColor: "var(--colour-border)",
                          backgroundColor: "var(--colour-surface-raised)",
                        }}
                      >
                        {d.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {data.badges.length > 0 && (
                <div className="mt-5">
                  <h4 className="text-[14px] font-semibold mb-2">Badges earned</h4>
                  <div className="space-y-2">
                    {data.badges.map((b) => (
                      <div
                        key={b.id}
                        className="p-3 rounded-[var(--radius-md)] border"
                        style={{
                          borderColor: "var(--colour-border)",
                          borderLeft: `4px solid ${b.colour_hex}`,
                          backgroundColor: "var(--colour-surface)",
                        }}
                      >
                        <div
                          className="text-[10px] uppercase tracking-wider font-bold"
                          style={{ color: rarityColour(b.rarity) }}
                        >
                          {b.rarity}
                        </div>
                        <div className="text-[14px] font-semibold">{b.name}</div>
                        <div className="text-[12px] text-[var(--colour-text-secondary)]">
                          {b.description}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
